/**
 * PDP Recently Viewed – tracks viewed products in localStorage
 * and renders them using the Section Rendering API with the
 * shared product-card snippet.
 */

class PdpRecentlyViewed extends HTMLElement {
  static STORAGE_KEY = 'zendiamond:recently-viewed';
  static MAX_STORED = 20;

  connectedCallback() {
    this.productHandle = this.dataset.productHandle || '';
    this.productsToShow = parseInt(this.dataset.productsToShow, 10) || 8;
    this.enableQuickAdd = this.dataset.enableQuickAdd !== 'false';
    this.cardSection = this.enableQuickAdd
      ? 'pdp-recently-viewed-product'
      : 'pdp-recently-viewed-product-plain';
    this.sectionElement = this.closest('[id^="shopify-section-"]');
    this.grid = this.querySelector('.section-pdp-recently-viewed__grid');

    /* Save current product to recently viewed list */
    if (this.productHandle) {
      this.saveProduct(this.productHandle);
    }

    /* Render recently viewed products */
    this.renderProducts();
  }

  saveProduct(handle) {
    let viewed = this.getStoredHandles();
    viewed = viewed.filter((h) => h !== handle);
    viewed.unshift(handle);
    viewed = viewed.slice(0, PdpRecentlyViewed.MAX_STORED);
    try {
      localStorage.setItem(PdpRecentlyViewed.STORAGE_KEY, JSON.stringify(viewed));
    } catch (e) {
      /* localStorage full or disabled */
    }
  }

  getStoredHandles() {
    try {
      return JSON.parse(localStorage.getItem(PdpRecentlyViewed.STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  async renderProducts() {
    if (!this.grid) return;

    const handles = this.getStoredHandles()
      .filter((h) => h !== this.productHandle)
      .slice(0, this.productsToShow);

    if (handles.length === 0) return;

    const results = await Promise.allSettled(handles.map((handle) => this.fetchCard(handle)));

    const cards = results.filter((r) => r.status === 'fulfilled' && r.value).map((r) => r.value);

    if (cards.length === 0) return;

    cards.forEach((html) => {
      this.grid.insertAdjacentHTML('beforeend', html);
    });

    /* Show the section */
    if (this.sectionElement) {
      this.sectionElement.style.display = '';
    }

    /* Initialise swatch listeners for injected cards */
    this.initSwatches();
  }

  async fetchCard(handle) {
    try {
      const resp = await fetch(`/products/${handle}?sections=${this.cardSection}`);
      if (!resp.ok) return null;

      const data = await resp.json();
      const sectionHtml = data[this.cardSection];
      if (!sectionHtml) return null;

      /* Parse the section wrapper and extract the product card */
      const temp = document.createElement('div');
      temp.innerHTML = sectionHtml;

      /* Fix QuickAdd modal IDs to prevent conflicts with other sections */
      temp.querySelectorAll('[data-modal^="#QuickAdd-"]').forEach((opener) => {
        opener.dataset.modal = opener.dataset.modal.replace('#QuickAdd-', '#QuickAdd-rv-');
      });
      temp.querySelectorAll('[id^="QuickAdd-"]').forEach((el) => {
        el.id = el.id.replace('QuickAdd-', 'QuickAdd-rv-');
      });
      temp.querySelectorAll('[id^="ModalClose-"]').forEach((el) => {
        el.id = el.id.replace('ModalClose-', 'ModalClose-rv-');
      });
      temp.querySelectorAll('[id^="QuickAddInfo-"]').forEach((el) => {
        el.id = el.id.replace('QuickAddInfo-', 'QuickAddInfo-rv-');
      });

      const card = temp.querySelector('.section-home-bestsellers__card');
      if (!card) return null;

      /* Ensure the card is visible */
      card.classList.add('is-visible');

      return card.outerHTML;
    } catch {
      return null;
    }
  }

  /* ---- Swatch selection (same logic as pdp-you-may-also-like) ---- */

  initSwatches() {
    this.querySelectorAll('.section-home-bestsellers__swatch[data-variant-id]').forEach((swatch) => {
      swatch.addEventListener('click', () => this.onSwatchClick(swatch));
    });
  }

  onSwatchClick(swatch) {
    const card = swatch.closest('.section-home-bestsellers__card');
    if (!card) return;

    const variantId = swatch.dataset.variantId;
    const isAvailable = swatch.dataset.available === 'true';
    const imageUrl = swatch.dataset.image;
    const imageSrcset = swatch.dataset.imageSrcset;
    const price = swatch.dataset.price;
    const comparePrice = swatch.dataset.comparePrice;

    /* Update swatch selection */
    card.querySelectorAll('.section-home-bestsellers__swatch').forEach((s) => {
      s.classList.remove('section-home-bestsellers__swatch--selected');
    });
    swatch.classList.add('section-home-bestsellers__swatch--selected');

    /* Update variant ID in the form */
    const variantInput = card.querySelector('input.product-variant-id');
    if (variantInput) {
      variantInput.value = variantId;
      variantInput.disabled = !isAvailable;
    }

    /* Update the submit button state */
    const submitBtn = card.querySelector('.section-home-bestsellers__add-to-cart-btn[type="submit"]');
    if (submitBtn) {
      const addText = submitBtn.dataset.addToCartText;
      const soldOutText = submitBtn.dataset.soldOutText;
      const labelSpan = submitBtn.querySelector('span:not(.sold-out-message):not(.loading__spinner)');

      if (isAvailable) {
        submitBtn.removeAttribute('disabled');
        if (labelSpan && addText) labelSpan.textContent = addText;
      } else {
        submitBtn.setAttribute('disabled', 'disabled');
        if (labelSpan && soldOutText) labelSpan.textContent = soldOutText;
      }
    }

    /* Update card image */
    if (imageUrl) {
      const img = card.querySelector('.section-home-bestsellers__card-img');
      if (img) {
        img.src = imageUrl;
        if (imageSrcset) img.srcset = imageSrcset;
      }
    }

    /* Update price display */
    if (price) {
      const priceEl = card.querySelector('.section-home-bestsellers__card-price');
      if (priceEl) priceEl.textContent = price;
    }

    const comparePriceEl = card.querySelector('.section-home-bestsellers__card-compare-price');
    if (comparePrice) {
      if (comparePriceEl) {
        comparePriceEl.textContent = comparePrice;
        comparePriceEl.style.display = '';
      } else {
        const pricesWrap = card.querySelector('.section-home-bestsellers__card-prices');
        if (pricesWrap) {
          const s = document.createElement('s');
          s.className = 'section-home-bestsellers__card-compare-price';
          s.textContent = comparePrice;
          pricesWrap.appendChild(s);
        }
      }
    } else if (comparePriceEl) {
      comparePriceEl.style.display = 'none';
    }
  }
}

if (!customElements.get('pdp-recently-viewed')) {
  customElements.define('pdp-recently-viewed', PdpRecentlyViewed);
}
